/**
 * The menus as a state machine on plain data (no canvas): a stack of screens, each with its cursor;
 * the rows each screen shows and what they edit; rebinding a key; the change list's scroll. One
 * `update` per 50 Hz frame. The painter (`paint.ts`) only reads it.
 *
 * Screens stack: options, controls, credits and changes open over the title or the pause menu, and
 * BACK returns to whichever it was. Opening one of them with nothing open puts the title under it.
 */

import type { Release } from '../../app/version';
import type { SettingsStore } from '../../settings';
import { changeLines, readSeen, type TextLine, writeSeen } from './content';
import type { MenuAction, MenuInput, MenuScreen } from './index';
import { CHANGE_PANE, CHANGE_ROWS, CHANGE_WIDTH, inside, LESS_X, rowRects, VALUE_X } from './layout';
import { OPTION_ROWS, OPTIONS_DEFAULTS } from './options';
import { actionName, isBindable, KEY_ACTIONS, type KeyAction, type PlayerIndex, rebind } from './rebind';
import { type MenuRow, screenRows } from './screens';
import { keyName } from './text';

interface Level {
  screen: MenuScreen;
  cursor: number;
}

/** How long a note (a key taken from another action, a reset) stays up: 3 s. */
const NOTE_FRAMES = 150;
const SUB_SCREENS: ReadonlySet<MenuScreen> = new Set(['options', 'controls', 'credits', 'changes']);

export class MenuModel {
  private stack: Level[] = [];
  /** The controls row waiting for a key, or null. */
  listening: { player: PlayerIndex; action: KeyAction } | null = null;
  /** A one-line message on the options or controls screen, until frame `until`. */
  note: { text: string; until: number } | null = null;
  /** The change list's first visible line. */
  scroll = 0;
  /** Frames since the menus were made (for flashing). */
  frame = 0;
  private seen: string | null = readSeen();
  private changes: TextLine[] | null = null;

  constructor(
    readonly store: SettingsStore,
    readonly releases: readonly Release[],
    readonly version: string,
  ) {}

  /** The screen showing, or null (in play). */
  get screen(): MenuScreen | null {
    return this.stack.at(-1)?.screen ?? null;
  }

  /** True while a screen covers the playfield (everything but the title's own menu). */
  get covering(): boolean {
    const s = this.screen;
    return s !== null && s !== 'title';
  }

  /** True while the controls screen waits for a key: the game must not act on that key (P, Q, F...). */
  get capturing(): boolean {
    return this.listening !== null;
  }

  /** The chosen row on the showing screen. */
  get cursor(): number {
    return this.stack.at(-1)?.cursor ?? 0;
  }

  /** This version's changes have not been opened yet: the title marks its version NEW. */
  get changesNew(): boolean {
    return this.seen !== this.version;
  }

  /** The title or the pause menu start afresh; the others open over whatever is showing. */
  open(screen: MenuScreen): void {
    this.listening = null;
    this.note = null;
    if (!SUB_SCREENS.has(screen)) {
      this.stack = [{ screen, cursor: 0 }];
      return;
    }
    if (this.screen === screen) return;
    if (this.stack.length === 0) this.stack = [{ screen: 'title', cursor: 0 }];
    this.push(screen);
  }

  close(): void {
    this.stack = [];
    this.listening = null;
    this.note = null;
  }

  /** One frame of input; returns what the session must do, if anything. */
  update(input: MenuInput): MenuAction | null {
    this.frame++;
    const top = this.stack.at(-1);
    if (!top) return null;
    if (this.listening) {
      this.listen(input);
      return null;
    }
    const rows = this.rows();
    if (input.pointer) {
      const hit = this.pointer(input.pointer, rows, top);
      if (hit !== undefined) return hit;
    }
    if (top.screen === 'title' && input.p2Fire) return this.finish({ type: 'start', players: 2 });
    if (top.screen === 'changes') return this.changesInput(input);
    const n = rows.length;
    if (input.up) top.cursor = (top.cursor - 1 + n) % n;
    if (input.down) top.cursor = (top.cursor + 1) % n;
    const current = rows[top.cursor];
    if (current && (input.left || input.right)) this.adjust(current, input.left ? -1 : 1);
    if (current && input.select) return this.activate(current);
    if (input.back) return this.back();
    return null;
  }

  /** The rows of the showing screen, with their values as of now. */
  rows(): MenuRow[] {
    return screenRows(this.screen, this.store.get(), {
      version: this.version, changesNew: this.changesNew, listening: this.listening,
    });
  }

  /** The chosen option's help line (the options screen), or ''. */
  help(): string {
    const id = this.rows()[this.cursor]?.id;
    return OPTION_ROWS.find((o) => o.id === id)?.help(this.store.get()) ?? '';
  }

  /** The change list, newest release first, wrapped to the screen. */
  changeList(): TextLine[] {
    this.changes ??= changeLines(this.releases, CHANGE_WIDTH);
    return this.changes;
  }

  /** The note, while it lasts. */
  noteText(): string {
    return this.note && this.frame < this.note.until ? this.note.text : '';
  }

  private say(text: string): void {
    this.note = { text, until: this.frame + NOTE_FRAMES };
  }

  private push(screen: MenuScreen): void {
    if (screen === 'changes') {
      this.scroll = 0;
      writeSeen(this.version);
      this.seen = this.version;
    }
    this.note = null;
    this.stack.push({ screen, cursor: 0 });
  }

  private finish(action: MenuAction): MenuAction {
    this.close();
    return action;
  }

  private back(): MenuAction | null {
    this.note = null;
    if (this.stack.length > 1) {
      this.stack.pop();
      return null;
    }
    return this.screen === 'pause' ? this.finish({ type: 'resume' }) : null;
  }

  private activate(r: MenuRow): MenuAction | null {
    switch (r.id) {
      case 'start1': return this.finish({ type: 'start', players: 1 });
      case 'start2': return this.finish({ type: 'start', players: 2 });
      case 'resume': return this.finish({ type: 'resume' });
      case 'quit': return this.finish({ type: 'quit' });
      case 'back': return this.back();
      case 'options': case 'controls': case 'credits': case 'changes':
        this.push(r.id);
        return null;
      case 'resetOptions':
        this.store.update(OPTIONS_DEFAULTS);
        this.say('OPTIONS BACK TO THE DEFAULTS');
        return null;
      case 'resetKeys':
        this.store.reset('keys');
        this.say('KEYS BACK TO THE DEFAULTS');
        return null;
      default:
        break;
    }
    if (r.kind === 'option') this.adjust(r, 1);
    if (r.kind === 'key') {
      const [, p, a] = r.id.split('.');
      const action = KEY_ACTIONS.find((k) => k === a);
      if (action) this.listening = { player: p === '1' ? 1 : 0, action };
      this.note = null;
    }
    return null;
  }

  private adjust(r: MenuRow, dir: -1 | 1): void {
    const patch = OPTION_ROWS.find((o) => o.id === r.id)?.step(this.store.get(), dir);
    if (patch) this.store.update(patch);
  }

  /** Waiting for a key: bind it by the clash rule, refuse a reserved one, Esc (or BACK) cancels. */
  private listen(input: MenuInput): void {
    const l = this.listening;
    if (!l) return;
    const key = input.key;
    if (key === 'Escape' || (input.back && (key === null || !isBindable(key))) || input.pointer?.click) {
      this.listening = null;
      return;
    }
    if (key === null) return;
    const result = rebind(this.store.get().keys, l.player, l.action, key);
    if (!result) {
      this.say(`${keyName(key)} CANNOT BE BOUND`);
      return;
    }
    this.store.update({ keys: result.keys });
    this.listening = null;
    const m = result.moved;
    if (!m) this.note = null;
    else if (m.swapped) this.say(`SWAPPED WITH ${actionName(m.player, m.action)}`);
    else this.say(`${keyName(key)} TAKEN FROM ${actionName(m.player, m.action)}`);
  }

  /** The mouse: hovering chooses a row, a click activates it (on an option's "<", steps it left). */
  private pointer(
    p: NonNullable<MenuInput['pointer']>, rows: readonly MenuRow[], top: Level,
  ): MenuAction | null | undefined {
    const index = rowRects(top.screen, rows.length).findIndex((r) => inside(r, p.x, p.y));
    const hit = rows[index];
    if (hit) top.cursor = index;
    if (!p.click) return undefined;
    if (hit) {
      if (hit.kind === 'option' && p.x >= LESS_X - 4 && p.x < VALUE_X - 2) {
        this.adjust(hit, -1);
        return null;
      }
      return this.activate(hit);
    }
    if (top.screen === 'changes' && inside(CHANGE_PANE, p.x, p.y)) {
      this.scrollBy(p.y < CHANGE_PANE.y + CHANGE_PANE.h / 2 ? -(CHANGE_ROWS - 1) : CHANGE_ROWS - 1);
      return null;
    }
    return undefined;
  }

  private changesInput(input: MenuInput): MenuAction | null {
    const page = CHANGE_ROWS - 1;
    if (input.up) this.scrollBy(-1);
    if (input.down) this.scrollBy(1);
    if (input.left) this.scrollBy(-page);
    if (input.right) this.scrollBy(page);
    return input.select || input.back ? this.back() : null;
  }

  private scrollBy(n: number): void {
    const max = Math.max(0, this.changeList().length - CHANGE_ROWS);
    this.scroll = Math.min(max, Math.max(0, this.scroll + n));
  }
}
